import ShapeBase from './ShapeBase';
import TailControl from '../controls/TailControl';
import { fabric } from 'fabric';
import tools from '../tools';
import shapeAnnotation from '../assets/icons/shapeAnnotation.svg';
import { svgToIcon } from '../utils/svg';
import { type TextProps } from '../objects/EmbeddedTextBehaviorsV2';
import type Shape from '../objects/Shape';
import { oncePerEvent } from '../utils/batching';
import SimpleSelection from '../fabric/SimpleSelection';
import type Canvas from '../objects/Canvas';
import { type IPoint } from 'fabric/fabric-impl';
import { type ImageLines } from '../external-types';

const isMultiSelect = (canvas: Canvas, event: Event) => {
  for (const key of canvas.selectionKey) {
    if (event[key]) return true;
  }
  return false;
};

class ShapeAnnotation extends ShapeBase {
  annotationLastLeft?: number;
  annotationLastTop?: number;

  textProps: TextProps = {
    allowedStyles: 'any',
    location: 'inside',
    textAlign: 'user',
    verticalAlign: 'middle',
    fontSize: 'user',
    overflow: 'visible',
    wrapText: 'user',
    margins: 'scale',
  };

  static renderIcon() {
    return svgToIcon(shapeAnnotation);
  }

  static defaultWidth() {
    return 200;
  }

  static defaultHeight() {
    return 30;
  }

  constructor(fabricObject: Shape) {
    super(fabricObject);

    fabricObject.uniformScaling = true;
    fabricObject.wrapText = true;
    fabricObject.strokeWidth = 4;
    fabricObject.fontSize ||= 14;
    fabricObject.strokeLineJoin = 'round';
    this.fabricObject.stroke = null;

    this.updateRelativeTailPosition();

    fabricObject.on('mousedown', () => {
      this.annotationLastLeft = fabricObject.left;
      this.annotationLastTop = fabricObject.top;
    });
    fabricObject.on('moving', e => {
      this.moveWithTail(e);
    });
    fabricObject.on('moved', e => {
      this.moveWithTail(e);
      this.annotationLastLeft = undefined;
      this.annotationLastTop = undefined;
    });

    // Special handling for the tail. If the user clicks the tail on an unselected
    // annotation we need to pre-select the annotation so that fabric then
    // treats the mouse-down as affecting the control. Otherwise we start dragging
    // the body of the annotation instead of the tail, which is not what the user
    // expects.
    fabricObject.on('mousedown:before', eventData => {
      const activeObject = fabricObject.canvas.getActiveObject();
      if (fabricObject === activeObject) return;

      const selectedObjects = activeObject?.getObjects() ?? [];

      // We have to preselect the object so _findTargetCorner works.
      fabricObject.canvas.setActiveObject(fabricObject, eventData.e);
      const corner = fabricObject._findTargetCorner(
        fabricObject.canvas.getPointer(eventData.e, true),
        fabric.util.isTouchEvent(eventData.e)
      );

      if (corner !== 'tail') {
        // Wasn't a click on the tail, revert to the default behavior.
        fabricObject.canvas.discardActiveObject();
        if (selectedObjects.length > 0) {
          fabricObject.canvas.setActiveObject(
            new SimpleSelection(selectedObjects, {
              canvas: fabricObject.canvas,
            }),
            eventData.e
          );
        }
      } else {
        if (
          selectedObjects.length > 0 &&
          isMultiSelect(fabricObject.canvas, eventData.e)
        ) {
          fabricObject.canvas.setActiveObject(
            new SimpleSelection(selectedObjects, {
              canvas: fabricObject.canvas,
            }),
            eventData.e
          );
        }
        fabricObject.canvas.requestRenderAll();
      }
    });
  }

  static shapeControls = {
    ...this.createDefaultControls({ noRotate: true }),
    tail: new TailControl({}),
  };

  updateRelativeTailPosition() {
    if (!this.hasRelativeTailPosition()) {
      if (this.hasLegacyTailPosition()) {
        // We no longer want to use tailOffsetX/tailOffsetY. This was the absolute
        // position of the tail on older whiteboards. We now use the relative
        // position tailRelativePosX/tailRelativePosY.
        // https://github.com/aha-app/aha-app/pull/32655
        this.fabricObject.tailRelativePosX =
          (this.fabricObject.tailOffsetX ?? 0) - this.fabricObject.left;
        this.fabricObject.tailRelativePosY =
          (this.fabricObject.tailOffsetY ?? 0) - this.fabricObject.top;
      } else {
        // This is a new shape, set the tail position to the left of the label.
        this.fabricObject.tailRelativePosX = -40;
        this.fabricObject.tailRelativePosY =
          (this.constructor as typeof ShapeBase).defaultHeight() / 2;
      }
    }
  }

  hasRelativeTailPosition() {
    return (
      this.fabricObject.tailRelativePosX !== undefined &&
      this.fabricObject.tailRelativePosY !== undefined
    );
  }

  hasLegacyTailPosition() {
    return (
      this.fabricObject.tailOffsetX !== undefined &&
      this.fabricObject.tailOffsetY !== undefined
    );
  }

  moveWithTail(e) {
    oncePerEvent(`${this.fabricObject.id}-moveWithTail`, false, () => {
      if (
        this.annotationLastLeft === undefined ||
        this.annotationLastTop === undefined
      ) {
        this.annotationLastLeft = e.transform.last.left;
        this.annotationLastTop = e.transform.last.top;
      }

      const movingWithGroup =
        this.fabricObject.simpleGroup &&
        this.canvas.getActiveObject() !== this.fabricObject;

      if (
        !this.fabricObject.isDraggingFromMenu &&
        !e.frameMoving &&
        !movingWithGroup
      ) {
        const tailRelativePosX =
          (this.fabricObject.tailRelativePosX ?? 0) +
          ((this.annotationLastLeft as number) - this.fabricObject.left);
        const tailRelativePosY =
          (this.fabricObject.tailRelativePosY ?? 0) +
          ((this.annotationLastTop as number) - this.fabricObject.top);

        this.annotationLastLeft = this.fabricObject.left;
        this.annotationLastTop = this.fabricObject.top;

        this.fabricObject.set({
          tailRelativePosX,
          tailRelativePosY,
        });
      }
    });
  }

  tools() {
    return [[tools.ToolFillColor]];
  }

  allowedStyleCopyPasteFields(): string[] {
    return ['opacity', 'fill'];
  }

  // No attachments on annotations.
  attachmentPoints() {
    return [];
  }

  static outboundConnectControlDefinitions() {
    return {};
  }

  containsPoint(
    pointer: IPoint,
    lines: ImageLines,
    absolute?: boolean,
    calculate?: boolean,
    superContains?: boolean
  ) {
    if (superContains) return true;

    // Translate the absolute mouse position to the viewport.
    const object = this.fabricObject;
    const adjustedPointer = object.canvas.restorePointerVpt(pointer);

    return (
      Math.hypot(
        adjustedPointer.x - (object.left + (object.tailRelativePosX ?? 0)),
        adjustedPointer.y - (object.top + (object.tailRelativePosY ?? 0))
      ) < 12
    );
  }

  isOnScreen(calculate: boolean, superIsOnScreen: boolean) {
    if (superIsOnScreen) return true;

    const object = this.fabricObject;
    const canvas = object.canvas;
    if (!canvas.vptCoords) {
      return false;
    }
    const pointTL = canvas.vptCoords.tl;
    const pointBR = canvas.vptCoords.br;

    return (
      object.left + (object.tailRelativePosX ?? 0) >= pointTL.x &&
      object.left + (object.tailRelativePosX ?? 0) <= pointBR.x &&
      object.top + (object.tailRelativePosY ?? 0) >= pointTL.y &&
      object.top + (object.tailRelativePosY ?? 0) <= pointBR.y
    );
  }

  cornerRadius() {
    return Math.min(5 * this.scaleX, 5 * this.scaleY);
  }

  renderArrowhead(
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ) {
    const scaleX = this.scaleX;
    const scaleY = this.scaleY;
    const cospix = 0.866025404;
    const dx = (x2 - x1) * scaleX;
    const dy = (y2 - y1) * scaleY;
    const length = Math.sqrt(dy * dy + dx * dx);
    const headlen = 10;
    const sina = dy / length;
    const cosa = dx / length;
    const cosp = cosa * cospix - 0.5 * sina;
    const cosm = cosa * cospix + 0.5 * sina;
    const sinp = cosa * 0.5 + cospix * sina;
    const sinm = cospix * sina - cosa * 0.5;

    ctx.beginPath();
    ctx.moveTo(x2 - (headlen * cosm) / scaleX, y2 - (headlen * sinm) / scaleY);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x2 - (headlen * cosp) / scaleX, y2 - (headlen * sinp) / scaleY);
    ctx.closePath();
    ctx.lineJoin = 'round';
    this.fabricObject._renderFill(ctx);
    this.fabricObject._renderStroke(ctx);
  }

  render(ctx: CanvasRenderingContext2D) {
    const object = this.fabricObject;
    const { scaleY, scaleX, width, height } = this.fabricObject;

    // Draw the rounded rectangle.
    ctx.save();
    if (this.fabricObject.canvas.getZoom() > 0.5) {
      ctx.shadowBlur = 8;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    }

    const w = width;
    const h = height;
    const top = -h / 2;
    const r = this.cornerRadius();
    const rx = r / scaleX;
    const ry = r / scaleY;

    ctx.beginPath();
    ctx.moveTo(-w / 2 + rx, top);
    ctx.lineTo(w / 2 - rx, top);
    ctx.ellipse(w / 2 - rx, top + ry, rx, ry, 0, -Math.PI / 2, 0);
    ctx.lineTo(w / 2, h / 2 - ry);
    ctx.ellipse(w / 2 - rx, h / 2 - ry, rx, ry, 0, 0, Math.PI / 2);
    ctx.lineTo(-w / 2 + rx, h / 2);
    ctx.ellipse(-w / 2 + rx, h / 2 - ry, rx, ry, 0, Math.PI / 2, Math.PI);
    ctx.lineTo(-w / 2, h / 2 - ry);
    ctx.ellipse(-w / 2 + rx, top + ry, rx, ry, 0, Math.PI, 1.5 * Math.PI);
    ctx.closePath();
    object._renderFill(ctx);
    ctx.restore();

    // Draw the tail.
    const tail = object.toLocalPoint(
      new fabric.Point(
        object.left + (object.tailRelativePosX ?? 0),
        object.top + (object.tailRelativePosY ?? 0)
      ),
      'center',
      'center'
    );
    tail.x = tail.x / scaleX;
    tail.y = tail.y / scaleY;

    // Set the origin so the line goes orthogonally if it can.
    const widthLimit = width / 2 - r / scaleX;
    const heightLimit = height / 2 - r / scaleY;
    const originX = Math.min(Math.max(tail.x, -widthLimit), widthLimit);
    const originY = Math.min(Math.max(tail.y, -heightLimit), heightLimit);

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(originX, originY);
    ctx.lineTo(tail.x, tail.y);
    object.stroke = object.fill as string;
    object._renderStroke(ctx);
    this.renderArrowhead(ctx, originX, originY, tail.x, tail.y);
    ctx.restore();
  }
}

export default ShapeAnnotation;
